import * as THREE from 'three';
import { saveDefaultsToSource, type SaveDefaultsResult } from '../dev/saveDefaults';
import handlingDefaults from './Handling.defaults.json';

export interface Handling {
  maxSpeed: number;
  accel: number;
  brakeStrength: number;
  drag: number;
  grip: number;
  turnRate: number;
  offTrackDrag: number;
  offTrackGrip: number;
  reverseSpeed: number;
  brakeGripMultiplier: number;
  brakeTurnMultiplier: number;
  steerCurve: number;
  lowSpeedSteerMultiplier: number;
  highSpeedSteerMultiplier: number;
  // Differential-drive (per-engine stick) tuning.
  // Yaw rate at full opposite engines (L=+1, R=-1) at rest. Bypasses the
  // speed-scaled steer factor so the racer can pivot in place. Research:
  // 360° pivot in 1.2-2.0s at rest reads as snappy-but-not-twitchy for
  // skid-steer / Virtual-On-style twin-stick games. 2.1 rad/s with the
  // ±1 stick range gives ~1.5s to a full 360° at full opposite.
  differentialYawRate: number;
  // High-speed attenuation: yaw rate scales by (1 - this·v/maxSpeed) at
  // speed, so differential becomes a turn at speed instead of a spin-out.
  differentialYawSpeedAttenuation: number;
  // Reverse strength as a fraction of forward accel when both sticks
  // pull negative. ATV/snowmobile convention is ~30%; arcade-racer feel.
  differentialReverseRatio: number;
  // Drift: lateral grip multiplier when engines are fully asymmetric
  // (one side reversed against the other). 1.0 = no drift (original
  // on-rails feel); 0.3 = pod slides ~3.3× longer through hard turns.
  // The drift only kicks in proportional to |engineDiff|, so straights
  // and gentle turns keep full grip.
  driftGripFactor: number;
  // Aircraft-style rotational physics. The pod is essentially a jet
  // that hovers — yaw is governed by torque (input from differential
  // thrust or rudder), rotational drag (air resists rotation), and a
  // weather-vane force that pulls the body toward its velocity vector
  // (the tail pointing into the wind, like a real aircraft). Symmetric
  // — same dynamics in both directions, no arcade-feel asymmetric LPF.
  //
  // rotationalDrag — how aggressively air drag opposes rotation. Higher
  //   = snappier response and faster decay, lower = more spin-like-a-top
  //   carry after release. Time constant ≈ 1/rotationalDrag seconds.
  // weatherVaneStrength — how aggressively the body's tail wants to
  //   point into the airflow. Scales with speed and slip angle. Higher
  //   = body realigns to velocity faster, lower = drifts persist
  //   longer. This is the *honest* alignment force, replacing the magic
  //   "yaw decays toward zero" LPF.
  rotationalDrag: number;
  weatherVaneStrength: number;
  // Turn-in carving: extra lateral grip while the body is actively
  // yawing. Scales with |yawVelocity| up to a saturation threshold.
  // This is the Wipeout "scrape the front into the corner" mechanic —
  // the harder you turn, the more grip you have, so velocity tracks
  // heading more aggressively in committed turns. Drift still happens
  // (when slip persists), but turn-in feels grippy instead of icy.
  turnInGripStrength: number;
  turnInGripYawThreshold: number;
  // Idle stop: when no input is held AND speed drops below a threshold,
  // a soft stop force grows toward a hard snap-to-zero. Solves the
  // "creeps forever after braking" problem that pure drag has at the
  // asymptote.
  idleStopSoftRate: number;       // multiplicative decay rate during the soft stage
  idleStopSoftSpeedCap: number;   // m/s; soft stage active below this
  idleStopHardSnapSpeed: number;  // m/s; below this, velocity snaps to 0
  // Brake taper at low speed: brake retains full force at speed but
  // ramps down toward zero near standstill so it can't push the body
  // backwards via overshoot or jitter from frame-rate quantization.
  brakeLowSpeedTaperRange: number; // m/s; brake scales 0..1 over this range
  // Air-brake drag spike rate. The legacy brake input doesn't subtract
  // forward velocity directly any more — it adds extra drag to the
  // *total* velocity vector (omnidirectional). That's the only
  // physically honest "brake" for a hover-craft: a flap that adds
  // aerodynamic drag, slowing you down regardless of whether you're
  // pointing in your direction of motion.
  airBrakeDragRate: number;
  // Drift boost: charges while the pod's velocity vector lags behind
  // its heading (high slip angle) and discharges as a brief thrust
  // burst when the drift ends. Diegetic model: the engine binders
  // compress under torque during a slide and snap back to release
  // stored kinetic energy. Sim-leaning — needs a real commitment to
  // the slip angle, not every gentle corner.
  driftChargeRate: number;             // charge accrual per second at full slip + speed + yaw
  driftChargeDecayRate: number;        // passive bleed when not drifting
  driftChargeYawSat: number;           // rad/s; yaw at which the yaw-factor saturates to 1
  driftSlipEnterThreshold: number;     // rad; cross above to start charging
  driftSlipExitThreshold: number;      // rad; cross below to discharge (hysteresis)
  driftBoostMinCharge: number;         // [0..1]; legacy — unused in hold-to-boost model
  driftBoostThrust: number;            // m/s² extra forward thrust while boosting
  driftBoostMaxDuration: number;       // legacy — unused in hold-to-boost model
  driftBoostMaxSpeedMultiplier: number;// speed cap × this while boosting
  boostConsumeRate: number;            // charge drained per second while boost is held
  // Wall / rail collision feel. Collision detection lives in Collision.ts; these
  // values shape the response after a hit so hard impacts bounce/deflect and
  // sustained contact becomes a stable grind instead of repeated mini-crashes.
  collisionRestitution: number;         // 0 = no bounce, 0.2 = bouncy arcade rail
  collisionSlideFriction: number;       // immediate tangent-speed loss on hard impact
  collisionContactFriction: number;     // per-second tangent drag while grinding
  collisionPersistentSeconds: number;   // time on same wall before grind mode
  collisionMinBounceSpeed: number;      // below this, contact sticks/slides
  collisionSeparationSpeed: number;     // max outward bounce speed in grind mode
  collisionSameNormalDot: number;       // normal similarity for same-wall cache
  collisionEngineKick: number;          // visual impulse into pod engine springs
  collisionEngineSqueeze: number;       // inward engine-pair squeeze on impact
  collisionCockpitPunch: number;        // cockpit/pitch visual impulse on impact
  // Per-second exponential decay applied to grind friction once contact
  // has been sustained past collisionPersistentSeconds. 0 = no decay; the
  // legacy "long scrape stays brutal" behavior. Higher values let the
  // racer slide off after a sustained scrape so a glancing contact does
  // not magnet them to the wall.
  collisionContactFrictionRecovery: number;
  // Hitstop & FOV punch on hard impacts.
  collisionHitstopMaxMs: number;        // cap on the hitstop dip duration
  collisionHitstopSpeedRef: number;     // m/s into the wall that maps to full hitstop
  collisionFovPunchDeg: number;         // peak FOV bump in degrees on a max-impact hit
  // Maximum velocity (m/s) that a single physics step may remove from the
  // racer on a hard hit. When set, big impacts bleed across multiple
  // frames instead of dumping in one — gives the camera time to follow
  // and removes the brick-wall stutter feel.
  collisionMaxFrameLoss: number;
}

/** @deprecated Use Handling. Kept for one release while call sites migrate. */
export type PhysicsConfig = Handling;

export const HANDLING_DEFAULTS_FILE = 'src/game/Handling.defaults.json' as const;

/** @deprecated Use HANDLING_DEFAULTS_FILE. Kept for one release while call sites migrate. */
export const PHYSICS_DEFAULTS_FILE = HANDLING_DEFAULTS_FILE;

export const DEFAULT_HANDLING: Handling = handlingDefaults as Handling;

/** @deprecated Use DEFAULT_HANDLING. Kept for one release while call sites migrate. */
export const DEFAULT_PHYSICS = DEFAULT_HANDLING;

/** Persist the live handling config to `Handling.defaults.json` via dev middleware. */
export function saveHandlingDefaultsToSource(config: Handling): Promise<SaveDefaultsResult> {
  return saveDefaultsToSource(HANDLING_DEFAULTS_FILE, config);
}

/** @deprecated Use saveHandlingDefaultsToSource. Kept for one release while call sites migrate. */
export const savePhysicsDefaultsToSource = saveHandlingDefaultsToSource;

/** Reset a live Handling config back to the committed defaults. */
export function applyDefaultHandling(config: Handling): void {
  Object.assign(config, DEFAULT_HANDLING);
}

/** @deprecated Use applyDefaultHandling. Kept for one release while call sites migrate. */
export const applyDefaultPhysics = applyDefaultHandling;

export interface RacerState {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  heading: number;
  /**
   * Angular velocity in rad/s. Tracked separately from heading so the
   * pod has rotational inertia: turn-in builds yaw rate over the time
   * constant set by `yawInertiaResponse`, and releasing the steer
   * decays angular velocity smoothly instead of stopping the rotation
   * dead. This is what gives the body weight in F-Zero / Wipeout-style
   * hover physics.
   */
  yawVelocity: number;
  /**
   * Drift boost charge meter, [0..1]. Fills while the body is slipping
   * (velocity vector lags heading by more than the entry threshold)
   * and the pod is moving with meaningful speed. Released as a thrust
   * burst (`boostTimer` becomes positive) when the slide ends.
   */
  driftCharge: number;
  /** Elapsed time of the current boost burst (seconds). Zero when no
   *  boost is active; nonzero while the player holds Enter and the
   *  meter has charge to spend. Used by visual systems (engine plume
   *  amplifier, body lurch impulse) to detect boost activation edges. */
  boostTimer: number;
  /** Hysteresis flag: are we currently in the "charging" drift state? */
  isDrifting: boolean;
  /**
   * Vertical velocity (m/s) used by airborne physics. Zero on the ground.
   * Integrated under gravity by the game-loop's ground-sync step when
   * `airborne` is true. Positive = rising, negative = falling.
   */
  verticalVelocity: number;
  /**
   * True when the chassis has left the track surface (off a jump, off a
   * crest where the slope drops away faster than gravity can keep us
   * glued). While true, position.y is not snapped to the ground; instead
   * gravity integrates verticalVelocity. Cleared on landing.
   */
  airborne: boolean;
}

export interface InputState {
  throttle: number;
  brake: number;
  steer: number;
  // Per-engine thrust in [-1, +1]. When either is set (non-undefined),
  // the differential-drive code path runs and throttle/brake/steer are
  // ignored. Sign convention: +1 = engine pushes body forward, -1 =
  // reverse. Asymmetric values produce yaw torque like real twin-engine
  // craft / tank tracks: stronger left engine pushes the left side
  // forward faster, rotating the racer toward screen-right.
  leftThrust?: number;
  rightThrust?: number;
  // Drift-boost trigger. When > 0.5 and the meter is at or above
  // driftBoostMinCharge, fires the stored boost as a thrust burst.
  // Manual fire (player chooses when to spend) — auto-discharge on
  // drift exit was removed because it auto-fired at unhelpful moments.
  boost?: number;
}

const FORWARD = new THREE.Vector3();
const RIGHT = new THREE.Vector3();
const TMP = new THREE.Vector3();

export function step(
  state: RacerState,
  input: InputState,
  config: PhysicsConfig,
  onTrack: boolean,
  dt: number,
): void {
  FORWARD.set(Math.sin(state.heading), 0, Math.cos(state.heading));
  RIGHT.set(Math.cos(state.heading), 0, -Math.sin(state.heading));

  let vForward = state.velocity.dot(FORWARD);
  let vLateral = state.velocity.dot(RIGHT);

  const hasPerEngine = input.leftThrust !== undefined || input.rightThrust !== undefined;
  const engL = THREE.MathUtils.clamp(input.leftThrust ?? 0, -1, 1);
  const engR = THREE.MathUtils.clamp(input.rightThrust ?? 0, -1, 1);
  const engineAvg = (engL + engR) * 0.5;
  const engineDiff = engL - engR;

  // Any engine pulled into reverse counts as braking — that's how the
  // pod's "air brake" naturally falls out of the per-engine model. This
  // engages the grip-boost path even on a full-opposite pivot (avg=0)
  // or a single-side reverse, so hard cornering with one engine pulled
  // back tightens the line like a Wipeout side-airbrake.
  const brakingFromEngines = hasPerEngine && Math.min(engL, engR) < -0.01 && vForward > 0;
  const isBraking = hasPerEngine ? brakingFromEngines : input.brake > 0;
  // Both sticks in the upper half (≥0) is the "throttle on" regime: the
  // pod is at full forward thrust regardless of how high each stick is,
  // and the difference between them is pure yaw input. Pulling either
  // stick negative drops out of this regime and engages air-brake math.
  const bothEnginesForward = hasPerEngine && engL >= 0 && engR >= 0;

  // Air drag — applies to the *total* velocity vector, not just the
  // forward axis. A hover-pod has no tires, so it has no preferred
  // direction of travel; air resistance opposes motion in whatever
  // direction the velocity vector points. This is what makes coasting
  // bleed speed even in a slide, and what makes the pod eventually
  // stop after a short engine tap.
  const drag = onTrack ? config.drag : config.offTrackDrag;
  let dragFactor = Math.max(0, 1 - drag * dt);
  // Air brake: legacy brake input adds an extra drag spike on top of
  // the base air drag — bleeds total velocity faster, regardless of
  // body orientation. Gated on airBrakeDragRate > 0 so pre-0.2.14
  // physics versions (which used a vForward retro-thrust brake) keep
  // their original behavior.
  const airBrakeActive = !hasPerEngine
    && input.brake > 0
    && vForward > 0
    && config.airBrakeDragRate > 0;
  if (airBrakeActive) {
    dragFactor *= Math.max(0, 1 - input.brake * config.airBrakeDragRate * dt);
  }
  // Lateral grip — "hover-field lateral coupling". Whatever mechanism
  // keeps the pod hovering also provides a self-righting pull toward
  // heading. Without it the pod slides on glass; with too much it's a
  // car. brakeGripMultiplier tightens lines under braking, driftSlip
  // drops grip when committing to a per-engine drift.
  // Drift commitment — unified signal for "player is asking to slide".
  // Two ways to trigger it:
  //   - Differential mode: asymmetric engine thrust (one reversed against
  //     the other). Strength scales with |engineDiff|.
  //   - Legacy WASD: brake while steering hard. Tap-brake-into-corner is
  //     the canonical arcade drift initiator. Strength scales with |steer|.
  // Either signal ramps driftAmount, which downstream cuts lateral grip,
  // neutralizes brake-grip multiplier, kills turn-in carving, and silences
  // weather-vane — so the slide can develop and persist instead of being
  // smothered by every alignment force in the system.
  const asymmetry = (hasPerEngine && !bothEnginesForward) ? Math.abs(engineDiff) * 0.5 : 0;
  const diffDriftAmount = THREE.MathUtils.smoothstep(asymmetry, 0.4, 0.95);
  const wasdDriftAmount = (!hasPerEngine && input.brake > 0)
    ? THREE.MathUtils.smoothstep(Math.abs(input.steer), 0.3, 0.85) * input.brake
    : 0;
  const driftAmount = Math.max(diffDriftAmount, wasdDriftAmount);
  const driftSlip = THREE.MathUtils.lerp(1, config.driftGripFactor, driftAmount);
  // Brake-grip multiplier tightens shallow corners but fights against a
  // committed drift if applied at full strength. Blend it toward 1.0 as
  // driftAmount grows so a brake-tap-into-corner gets slip instead of
  // extra grip.
  const brakeGripBlended = isBraking
    ? THREE.MathUtils.lerp(config.brakeGripMultiplier, 1, driftAmount)
    : 1;
  // Turn-in carving: yaw activity adds extra lateral grip on top of the
  // baseline. The Wipeout "scrape the front into the corner" mechanic —
  // the harder you turn, the more grip you have, so velocity tracks
  // heading aggressively in committed turns. Saturates at the threshold
  // so wild spins don't get infinite grip. Gated by (1 - driftAmount)
  // so a committed drift doesn't fight itself — the player is choosing
  // slip, not asking for carving.
  const yawIntensity = config.turnInGripYawThreshold > 0
    ? Math.min(1, Math.abs(state.yawVelocity) / config.turnInGripYawThreshold)
    : 0;
  const turnInGrip = config.turnInGripStrength * yawIntensity * (1 - driftAmount);
  const grip = (onTrack ? config.grip : config.offTrackGrip)
    * driftSlip
    * brakeGripBlended
    + turnInGrip;

  vForward *= dragFactor;
  vLateral *= dragFactor;
  vLateral *= Math.max(0, 1 - grip * dt);

  if (hasPerEngine) {
    if (bothEnginesForward) {
      // "Hold forward = full speed". Both engines in [0,+1] = max thrust.
      vForward += config.accel * dt;
    } else if (engineAvg >= 0) {
      // One engine reversed but average still positive: scale thrust by
      // the average so reversing one stick bleeds power as expected.
      vForward += engineAvg * config.accel * dt;
    } else if (vForward > 0) {
      // Net negative thrust while moving forward: per-engine retro
      // thrust on vForward (engines actually pushing backwards). This
      // is *separate* from the legacy air-brake drag spike — it's a
      // physical thrust force rather than aero drag.
      vForward += engineAvg * config.brakeStrength * dt;
      if (vForward < 0) vForward = 0;
    } else {
      // Already stopped or reversing: build reverse, capped by reverseSpeed.
      vForward += engineAvg * config.accel * config.differentialReverseRatio * dt;
      vForward = Math.max(vForward, -config.reverseSpeed);
    }
  } else {
    vForward += input.throttle * config.accel * dt;
    if (input.brake > 0) {
      if (vForward > 0 && !airBrakeActive) {
        // Pre-0.2.14 retro-thrust brake (only when air-brake drag is off).
        // Taper near zero so brake doesn't push the body backwards from
        // overshoot or jitter when speed drops below the per-frame
        // brake delta.
        const taper = config.brakeLowSpeedTaperRange > 0
          ? Math.min(1, vForward / config.brakeLowSpeedTaperRange)
          : 1;
        vForward -= input.brake * config.brakeStrength * taper * dt;
        if (vForward < 0) vForward = 0;
      } else if (vForward <= 0) {
        // Reverse-from-rest (both old and new models).
        vForward -= input.brake * config.accel * 0.6 * dt;
        vForward = Math.max(vForward, -config.reverseSpeed);
      }
    }
  }

  // Idle stop: when no input is held and speed is below a threshold,
  // apply a soft stopping force, then snap to zero below a hard cutoff.
  // Without this the asymptotic drag bleed leaves the pod creeping
  // forever after a brake. Only fires when the player has truly let go
  // — any meaningful input keeps the soft stop disabled.
  const noPlayerInput = input.throttle < 0.01
    && input.brake < 0.01
    && Math.abs(input.steer) < 0.01
    && (input.leftThrust === undefined || Math.abs(input.leftThrust) < 0.01)
    && (input.rightThrust === undefined || Math.abs(input.rightThrust) < 0.01);
  if (noPlayerInput) {
    const idleSpeed = Math.hypot(vForward, vLateral);
    if (idleSpeed <= config.idleStopHardSnapSpeed) {
      vForward = 0;
      vLateral = 0;
    } else if (idleSpeed < config.idleStopSoftSpeedCap) {
      // Soft brake: scales up as speed approaches zero so the stopping
      // force is gentle far from rest, firmer near it.
      const closeness = 1 - idleSpeed / config.idleStopSoftSpeedCap;
      const softFactor = Math.max(0, 1 - config.idleStopSoftRate * closeness * dt);
      vForward *= softFactor;
      vLateral *= softFactor;
    }
  }

  const speed = Math.hypot(vForward, vLateral);
  const speedNorm = THREE.MathUtils.clamp(speed / config.maxSpeed, 0, 1);

  // Aircraft-style yaw dynamics: torque → angular velocity → heading.
  //
  // Three torque sources sum into the angular acceleration:
  //   1. Input torque  — engines / rudder pushing the body to rotate.
  //      Calibrated so equilibrium yaw rate matches the existing
  //      steady-state values when only drag is fighting it.
  //   2. Rotational drag  — air resists rotation, single rate, same
  //      direction as decay. This gives the body its rotational
  //      momentum: at low rotationalDrag the pod keeps spinning after
  //      input release until drag bleeds the angular velocity (the
  //      "spin like a top" feel emerges naturally).
  //   3. Weather-vane  — aerodynamic torque that pulls the tail into
  //      the airflow. Body wants to face its velocity direction. Scales
  //      with speed × slip angle, so it's strong at cruise and weak at
  //      low speed (donuts at standstill spin freely; spin-outs at
  //      cruise self-correct). This replaces the old "decay toward 0"
  //      magic with a real physical alignment force.
  let inputSteadyYawRate = 0;
  if (hasPerEngine) {
    const speedAtten = 1 - config.differentialYawSpeedAttenuation * speedNorm;
    inputSteadyYawRate = -engineDiff * config.differentialYawRate * speedAtten;
  } else {
    const lowSpeedFactor = THREE.MathUtils.lerp(
      config.lowSpeedSteerMultiplier,
      1,
      THREE.MathUtils.smoothstep(speedNorm, 0, 0.18),
    );
    const highSpeedFalloff = THREE.MathUtils.lerp(
      1,
      config.highSpeedSteerMultiplier,
      THREE.MathUtils.smoothstep(speedNorm, 0.35, 1),
    );
    const speedFactor = lowSpeedFactor * highSpeedFalloff;
    const steerInput = Math.sign(input.steer) * Math.pow(Math.abs(input.steer), config.steerCurve);
    const turnRate = config.turnRate * (input.brake > 0 ? config.brakeTurnMultiplier : 1);
    inputSteadyYawRate = -steerInput * turnRate * speedFactor;
  }
  // Slip angle: signed, body forward → velocity vector. Positive when
  // velocity is on the body's right (vLateral > 0). Body needs to
  // rotate right (heading-decreasing) to reduce positive slip — so
  // weather-vane torque is +slipAngle (pushes yawVelocity positive,
  // rotates body to the right via heading +=). Use total speed for
  // the "is moving" gate so the calc keeps working past 90° slip
  // (body rotated backwards relative to motion); atan2 handles the
  // wrap correctly.
  const slipAngle = speed > 0.5 ? Math.atan2(vLateral, vForward) : 0;
  // Convert "input target rate" (rad/s) to torque (rad/s²) via the
  // drag rate: at equilibrium yawVel = inputTorque / rotationalDrag.
  // So inputTorque = targetRate × drag preserves the historical
  // steady-state values.
  const inputTorque = inputSteadyYawRate * config.rotationalDrag;
  const rotDragTorque = -state.yawVelocity * config.rotationalDrag;
  // Weather-vane is the alignment force that pulls the body to face
  // the velocity vector. Great for normal flight feel, but it actively
  // suppresses drift — at moderate slip angles it can cancel the player's
  // input torque entirely. Gate it by (1 - driftAmount) so committed
  // drifts (differential or brake-tap) get no alignment fight, while
  // normal cornering still gets the aircraft alignment behavior.
  const weatherVaneTorque = slipAngle * config.weatherVaneStrength * speedNorm * (1 - driftAmount);
  state.yawVelocity += (inputTorque + rotDragTorque + weatherVaneTorque) * dt;
  state.heading += state.yawVelocity * dt;

  // Drift boost charge / discharge. Slip angle is the angle between
  // the body's heading and the velocity vector — high while sliding
  // sideways, low when going straight. We use the *post-thrust*
  // vForward/vLateral so the meter reflects what the pod is actually
  // doing this frame, not what it was doing last frame. Hysteresis on
  // entry/exit thresholds prevents the meter from flickering as slip
  // wobbles around the boundary; min-charge gate keeps tiny brushes
  // with drift from triggering trivial boosts.
  const slipMag = vForward > 0.5
    ? Math.abs(Math.atan2(vLateral, vForward))
    : 0;
  if (state.isDrifting) {
    if (slipMag < config.driftSlipExitThreshold) state.isDrifting = false;
  } else {
    if (slipMag > config.driftSlipEnterThreshold) state.isDrifting = true;
  }
  if (state.isDrifting) {
    // Charge scales with three factors that all reward "real" drift:
    //   - slipFactor: how committed the slip is (deep drifts > brushes)
    //   - speedNorm:  faster = more rewarding
    //   - yawFactor:  active rotation, not just sliding straight. This
    //                 is what the user wants — the harder you're turning
    //                 (faster yaw rate), the faster the meter fills.
    const slipFactor = THREE.MathUtils.clamp(
      (slipMag - config.driftSlipEnterThreshold) / 0.45,
      0,
      1,
    );
    const yawFactor = config.driftChargeYawSat > 0
      ? Math.min(1, Math.abs(state.yawVelocity) / config.driftChargeYawSat)
      : 1;
    state.driftCharge = Math.min(
      1,
      state.driftCharge + config.driftChargeRate * slipFactor * speedNorm * yawFactor * dt,
    );
  } else if (state.boostTimer <= 0) {
    // Not drifting and no active boost: charge bleeds passively (can't
    // bank forever, has to be used or it leaks away).
    state.driftCharge = Math.max(
      0,
      state.driftCharge - config.driftChargeDecayRate * dt,
    );
  }
  // Hold-to-boost (F-Zero GX model). While the boost button is held
  // and there's charge in the meter, spend it at boostConsumeRate and
  // apply constant boost output. Release the button to stop — any
  // unused charge stays banked for next time. No tiers, no
  // proportional scaling, no minimum charge: just hold and go.
  const boostHeld = (input.boost ?? 0) > 0.5;
  const boostActive = boostHeld && state.driftCharge > 0;
  let activeMaxSpeed = config.maxSpeed;
  if (boostActive) {
    state.driftCharge = Math.max(0, state.driftCharge - config.boostConsumeRate * dt);
    vForward += config.driftBoostThrust * dt;
    activeMaxSpeed = config.maxSpeed * config.driftBoostMaxSpeedMultiplier;
    state.boostTimer += dt;
  } else {
    state.boostTimer = 0;
  }

  state.velocity
    .copy(FORWARD).multiplyScalar(vForward)
    .add(TMP.copy(RIGHT).multiplyScalar(vLateral));

  const horizSpeed = Math.hypot(state.velocity.x, state.velocity.z);
  if (horizSpeed > activeMaxSpeed) {
    const k = activeMaxSpeed / horizSpeed;
    state.velocity.x *= k;
    state.velocity.z *= k;
  }

  state.position.x += state.velocity.x * dt;
  state.position.z += state.velocity.z * dt;
}

export function speed(state: RacerState): number {
  return Math.hypot(state.velocity.x, state.velocity.z);
}

export function driftAudioSignal(state: RacerState, config: Handling): number {
  const forwardX = Math.sin(state.heading);
  const forwardZ = Math.cos(state.heading);
  const rightX = Math.cos(state.heading);
  const rightZ = -Math.sin(state.heading);
  const vForward = state.velocity.x * forwardX + state.velocity.z * forwardZ;
  const vLateral = state.velocity.x * rightX + state.velocity.z * rightZ;
  const slipMag = vForward > 0.5 ? Math.abs(Math.atan2(vLateral, vForward)) : 0;
  const speedNorm = THREE.MathUtils.clamp(speed(state) / Math.max(1, config.maxSpeed), 0, 1);
  const slipFactor = THREE.MathUtils.clamp(
    (slipMag - config.driftSlipEnterThreshold) / 0.45,
    0,
    1,
  );
  const yawFactor = config.driftChargeYawSat > 0
    ? Math.min(1, Math.abs(state.yawVelocity) / config.driftChargeYawSat)
    : 1;
  return state.isDrifting ? Math.sqrt(slipFactor * speedNorm * yawFactor) : 0;
}
